import {
    FaProjectDiagram, FaShieldAlt, FaVial, FaServer, FaCogs,
    FaHeartbeat, FaCloud, FaCodeBranch, FaDatabase, FaRandom, FaChartBar,
    FaRobot
} from "react-icons/fa";

const iconsMap = {
    "System Design & Leadership": FaProjectDiagram,
    "Secure API & Web Application Security": FaShieldAlt,
    "Test Automation & SDET": FaVial,
    "DevOps Engineering - Scalable Deployments": FaServer,
    "Platform & CI/CD Engineering": FaCogs,
    "Site Reliability Engineering (SRE)": FaHeartbeat,
    "Cloud Engineering with AWS": FaCloud,
    "Automation Engineering & Infrastructure as Code (IaC)": FaCodeBranch,
    "Data Engineering Project - ETL Pipelines": FaDatabase,
    "ETL & Data Transformation Tools": FaRandom,
    "Analytics Engineering & Dashboard Delivery": FaChartBar,
    "AI Integration Engineer – Backend + AI Systems": FaRobot
};

const enginuityCommunity = {
    title: "Next Specialization Track - Enginuity Community Powers",
    description: `This advanced specialization track is powered by the collective experience and demand of the Enginuity Systems community. Designed for graduates of Semester One & Two, this path supports both Non-CSE and CSE learners who want to evolve beyond backend engineering into high-impact roles across modern software and infrastructure domains.
    It transforms graduates into versatile, industry- ready engineers by bridging backend engineering with career - focused specializations such as Engineers targeting roles like Staff Engineer, AI integration, SRE, Tech Lead, DevOps, System Architect, Application Security Engineering, Data Engineering and
    aiming for startup CTO or indie SaaS creator`,
    color: "border-red-300 bg-red-50",
    modules: [
        {
            title: "System Design & Leadership",
            description:
                "Become a Staff Engineer or Tech Lead by mastering distributed system design, domain-driven design (DDD), service orchestration, and high-scale backend architecture. Includes team code quality practices and tech mentoring strategies."
        },
        {
            title: "Secure API & Web Application Security",
            description:
                "Learn to secure backend systems against real-world attacks. Covers OWASP Top 10, token handling, CSRF/XSS prevention, CORS/CSP hardening, secure API gateway patterns, and penetration testing tools like ZAP and Burp Suite."
        },
        {
            title: "Test Automation & SDET",
            description:
                "Learn how to build complete testing pipelines with unit, integration, and E2E testing using Postman, Jest, Playwright, and GitHub Actions. Become a QA lead or SDET by integrating testing with CI/CD workflows and coverage strategies."
        },
        {
            title: "DevOps Engineering - Scalable Deployments",
            description:
                "Master infrastructure automation, cloud deployment, and CI/CD pipelines using Docker, GitHub Actions, and AWS services. Learn auto-scaling, load balancing, zero-downtime deployments, and secure DevOps practices."
        },
        {
            title: "Platform & CI/CD Engineering",
            description:
                "Design and maintain CI/CD platforms, reusable workflows, DevOps tooling, and secrets management at scale. Learn advanced GitHub Actions, reusable pipelines, artifact publishing, and secure environment setup."
        },
        {
            title: "Site Reliability Engineering (SRE)",
            description:
                "Focus on high availability, fault tolerance, observability, and incident management. Learn monitoring with Prometheus, Grafana, and alerting strategies for mission-critical applications in production."
        },
        {
            title: "Cloud Engineering with AWS",
            description:
                "Dive deep into AWS: EC2, S3, ECS, IAM, RDS, VPC, CloudWatch, Auto Scaling Groups. Learn to build production-grade infrastructure with security, performance, and cost-efficiency in mind."
        },
        {
            title: "Automation Engineering & Infrastructure as Code (IaC)",
            description:
                "Automate infrastructure provisioning using Terraform. Learn to write IaC for AWS resources, manage state, use modules, handle environments, and trigger deployments through GitOps and CI/CD."
        },
        {
            title: "Data Engineering Project - ETL Pipelines",
            description:
                "Design and build modern ETL pipelines using Python, SQL, Airflow, and AWS services. Ingest, transform, and load large datasets for analytics systems with proper error handling and retry logic."
        },
        {
            title: "ETL & Data Transformation Tools",
            description:
                "Focus on real-time and batch data processing. Use tools like Apache Kafka, Pandas, dbt, and AWS Lambda to clean, enrich, and transform data. Learn job scheduling and monitoring techniques."
        },
        {
            title: "Analytics Engineering & Dashboard Delivery",
            description:
                "Bridge engineering and analysis by building semantic data models, metrics layers, and dashboards. Use SQL, dbt, Metabase, and Superset to deliver data-driven insights to product and business teams."
        },
        {
            title: "AI Integration Engineer – Backend + AI Systems",
            description:
                "Learn to connect backend logic with AI tools like OpenAI API, LangChain, and LLM-based pipelines. Build intelligent features such as AI-powered chat, summarization, and recommendations. Tools include Python bridges, Redis, Pinecone, and FastAPI."
        }
    ]
};

const benefits = [
    "Pivot into high-demand fields (System Architect, Security Engineer, AI Integration Engineer, DevOps, SRE, QA Lead etc)",
    "Align skills with real-world roles and salaries",
    "Gain portfolio-worthy, team-driven projects",
    "Be mentored by experts within the Enginuity community",
    "Stay future-ready with critical technologies like AI APIs, Kubernetes, Kafka, Terraform and more...."
];

export default function EnginuityCommunitySection() {
    return (
        <>
            <section className="bg-red-50 border border-red-300 rounded-2xl px-6 py-10 my-10 shadow-md">
                <h2 className="text-3xl sm:text-4xl font-bold text-red-600 mb-6">
                    {enginuityCommunity.title}
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    {enginuityCommunity.description}
                </p>
                <section className="bg-white border border-gray-200 rounded-2xl px-6 py-10 md:p-12 shadow-sm my-10">
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                        <span className="text-red-500 text-4xl">🚀</span> Why This Track Matters
                    </h2>
                    <p className="text-gray-700 text-lg mb-6 max-w-3xl leading-relaxed">
                        As students complete their core backend foundations, they often want to go deeper,
                        specialize, or shift roles. This track allows them to:
                    </p>

                    <ul className="space-y-4">
                        {benefits.map((item, index) => (
                            <li
                                key={index}
                                className="flex items-start gap-3 text-gray-800 text-base sm:text-lg leading-relaxed"
                            >
                                <span className="text-green-500 mt-1 text-xl">✓</span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </section>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {enginuityCommunity.modules.map((module, index) => {
                        const Icon =
                            iconsMap[module.title.replace(/🔧|🧪|📊|📁|📦|🔐|🤖|🛠️|🚀|📡|💡/g, "").trim()] ||
                            FaCogs;

                        return (
                            <div
                                key={index}
                                className="bg-white border border-gray-200 rounded-xl p-5 shadow hover:shadow-lg transition"
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <Icon className="text-red-500 text-xl" />
                                    <h3 className="text-lg font-semibold text-red-700">{module.title}</h3>
                                </div>
                                <p className="text-gray-700 text-sm leading-relaxed">{module.description}</p>
                            </div>
                        );
                    })}
                </div>
            </section>
        </>
        
    );
}